import { theme } from "twin.macro";
import Caption from "../Caption";
import {
  StyledDividerTagsVR3,
  StyledTagsVR3Gradient,
  StyledTagsVR3Root,
  StyledTagsVR3BlackSale,
  StyledTagsVR3Primaton,
  SyleDividerDotTagsVR3,
} from "./styles";
import { darkenHex } from "@/utils/color";
import Tooltip, { TooltipContent, TooltipTrigger } from "@/components/Atoms/Tooltip";
import Paragraph from "../Paragraph";
import { useMemo } from "react";
import { TYPE_DISCOUNT } from "@/services/checkout/type";

interface TagsVR3Props {
  label: string | string[] | undefined;
  color?: string;
  type:
    | "customLabel"
    | "discount"
    | "sponsor"
    | "free"
    | TYPE_DISCOUNT.CUSTOMER_DISCOUNT
    | TYPE_DISCOUNT.PROMOFIT
    | TYPE_DISCOUNT.EXPIRATION_DISCOUNT
    | TYPE_DISCOUNT.FLASH
    | TYPE_DISCOUNT.BLACK_SALE
    | TYPE_DISCOUNT.PRIMATON;
  separator?: "line" | "dot";
}

const colorsByType = {
  sponsor: {
    separatorColor: theme`colors.secondary.40`,
    background: theme`colors.neutral.90`,
    borderColor: theme`colors.neutral.70`,
    textColor: theme`colors.neutral.30`,
  },
  free: {
    separatorColor: "transparent",
    background: theme`colors.tertiary.60`,
    borderColor: "transparent",
    textColor: theme`colors.neutral.100`,
  },
};

const TagsVR3 = ({ label, color, type, separator = "line" }: TagsVR3Props) => {
  const hasSponsor = type === "sponsor";
  const hasAntimerma = type === TYPE_DISCOUNT.EXPIRATION_DISCOUNT && color;
  const has_black_discount = type === TYPE_DISCOUNT.BLACK_SALE;
  const has_primaton = type === TYPE_DISCOUNT.PRIMATON;
  const hasExclusiveDiscount =
    type === TYPE_DISCOUNT.CUSTOMER_DISCOUNT ||
    type === TYPE_DISCOUNT.PROMOFIT ||
    type === TYPE_DISCOUNT.FLASH ||
    type === "discount";

  const colors = useMemo(() => {
    if (hasSponsor || type === "free") {
      return {
        assignedColor: colorsByType[type].separatorColor,
        assignedBackground: colorsByType[type].background,
        borderColor: color,
        textColor: colorsByType[type].textColor,
      };
    }

    // black sale
    if (has_black_discount) {
      return {
        assignedColor: theme`colors.neutral.0`,
        assignedBackground: theme`colors.neutral.0`,
        borderColor: theme`colors.neutral.0`,
        textColor: theme`colors.neutral.0`,
      };
    }

    if (has_primaton) {
      return {
        assignedColor: theme`colors.error.40`,
        assignedBackground: theme`colors.error.90`,
        borderColor: theme`colors.error.40`,
        textColor: theme`colors.error.20`,
      };
    }

    if (!hasExclusiveDiscount && color) {
      return {
        assignedColor: color,
        assignedBackground: color,
        borderColor: color,
        textColor: darkenHex(color, 0.7),
      };
    }

    return {
      assignedColor: theme`colors.secondary.50`,
      assignedBackground: theme`colors.secondary.50`,
      borderColor: theme`colors.secondary.50`,
      textColor: theme`colors.secondary.50`,
    };
  }, [type, color, hasSponsor, hasAntimerma, has_black_discount, has_primaton, hasExclusiveDiscount]);

  const assignedColor = colors.assignedColor;
  const assignedBackground = colors.assignedBackground;
  const assignedBorderColor = colors.borderColor;
  const assignedTextColor = colors.textColor;

  const renderLabelContent = () => {
    if (Array.isArray(label)) {
      return label.map((item, index) => {
        const isLast = index === label.length - 1;
        return (
          <div key={index} tw="flex h-full gap-s">
            <Caption fontWeight={700} tw="w-max" color={assignedTextColor}>
              {item}
            </Caption>
            {!isLast &&
              (separator === "line" ? (
                <StyledDividerTagsVR3 $color={assignedColor} />
              ) : (
                <SyleDividerDotTagsVR3 $color={assignedColor} />
              ))}
          </div>
        );
      });
    }

    return (
      <Caption fontWeight={700} color={assignedTextColor}>
        {label ?? ""}
      </Caption>
    );
  };

  // If is BLACK_SALE, use the black gradient component
  if (has_black_discount) {
    return <StyledTagsVR3BlackSale>{renderLabelContent()}</StyledTagsVR3BlackSale>;
  }

  // If is PRIMATON, use the dedicated primaton gradient component
  if (has_primaton) {
    return <StyledTagsVR3Primaton>{renderLabelContent()}</StyledTagsVR3Primaton>;
  }

  // If is a exclusive discount or a new discount without color apply gradient
  if (hasExclusiveDiscount || ((!hasSponsor || !hasAntimerma || type !== "free") && !color)) {
    return <StyledTagsVR3Gradient>{renderLabelContent()}</StyledTagsVR3Gradient>;
  }

  const tagContent = (
    <StyledTagsVR3Root
      $borderStyle={hasSponsor ? "solid" : "dashed"}
      $useTransparent={!!hasAntimerma}
      $background={assignedBackground}
      $borderColor={assignedBorderColor}
    >
      {renderLabelContent()}
    </StyledTagsVR3Root>
  );

  return hasAntimerma ? (
    <Tooltip placement="bottom-end">
      <TooltipTrigger>{tagContent}</TooltipTrigger>
      <TooltipContent tw="max-w-[14.313rem] z-10">
        <Paragraph sizeVariant="sm">Producto en excelente estado, con pronta vida útil.</Paragraph>
      </TooltipContent>
    </Tooltip>
  ) : (
    tagContent
  );
};

export default TagsVR3;
